import React, { useState, useContext, useMemo } from 'react';
import { StudentContext } from '../StudentContext';
import { addCatechist, updateCatechist } from '../GoogleSheetsService';

const CatechistManager = () => {
  const { catechistsData, refreshData, classMap, allBlocks } = useContext(StudentContext);

  const [isAddCatechistModalOpen, setIsAddCatechistModalOpen] = useState(false);
  const [newCatechist, setNewCatechist] = useState({
    idCode: '',
    holyName: '',
    fullName: '',
    username: '',
  });
  const [isAddingCatechist, setIsAddingCatechist] = useState(false);
  const [addCatechistError, setAddCatechistError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState({});

  // Danh sách các khối trưởng
  const blockLeaderOptions = [
    { value: '', label: 'Không' },
    { value: 'Khối Khai Tâm', label: 'Khối Khai Tâm' },
    { value: 'Khối Rước Lễ', label: 'Khối Rước Lễ' },
    { value: 'Khối Thêm Sức', label: 'Khối Thêm Sức' },
    { value: 'Khối Bao Đồng', label: 'Khối Bao Đồng' },
  ];

  // Danh sách các lớp
  const classOptions = useMemo(() => {
    const options = [{ value: '', label: 'Không' }];
    for (const blockId in allBlocks) {
      allBlocks[blockId].forEach((classItem) => {
        options.push({ value: classMap[classItem.id], label: classMap[classItem.id] });
      });
    }
    return options;
  }, [allBlocks, classMap]);

  const handleOpenAddCatechistModal = () => {
    const generatedIdCode = `GLV${(catechistsData.length + 2) < 100 ? '0' : ''}${catechistsData.length + 2}`;
    setNewCatechist({ idCode: generatedIdCode, holyName: '', fullName: '', username: '' });
    setAddCatechistError('');
    setIsAddCatechistModalOpen(true);
  };

  const handleNewCatechistChange = (e) => {
    const { name, value } = e.target;
    setNewCatechist((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewCatechistSubmit = async (e) => {
    e.preventDefault();
    setAddCatechistError('');
    if (!newCatechist.holyName.trim() || !newCatechist.fullName.trim() || !newCatechist.username.trim()) {
      setAddCatechistError('Vui lòng điền đầy đủ Tên Thánh, Họ và Tên, và Username.');
      return;
    }
    setIsAddingCatechist(true);
    try {
      const response = await addCatechist(newCatechist);
      if (response.success) {
        setIsAddCatechistModalOpen(false);
        alert('Thêm giáo lý viên thành công!');
        if (refreshData) refreshData();
      } else {
        setAddCatechistError(response.message || 'Lỗi không xác định từ server.');
      }
    } catch (error) {
      console.error('Error adding catechist:', error);
      setAddCatechistError(error.message || 'Lỗi khi thêm giáo lý viên. Vui lòng thử lại.');
    } finally {
      setIsAddingCatechist(false);
    }
  };

  const handleUpdateCatechist = async (idCode, field, value) => {
    setIsUpdating((prev) => ({ ...prev, [`${idCode}-${field}`]: true }));
    try {
      const updates = { [field]: value };
      const response = await updateCatechist(idCode, updates);
      if (response.success) {
        alert('Cập nhật thành công!');
        if (refreshData) refreshData();
      } else {
        alert(response.message || 'Lỗi khi cập nhật.');
      }
    } catch (error) {
      console.error(`Error updating catechist ${field}:`, error);
      alert(error.message || 'Lỗi khi cập nhật. Vui lòng thử lại.');
    } finally {
      setIsUpdating((prev) => ({ ...prev, [`${idCode}-${field}`]: false }));
    }
  };

  const filteredCatechists = useMemo(() => {
    if (!catechistsData) return [];
    if (!searchTerm.trim()) return catechistsData;
    return catechistsData.filter(catechist =>
      catechist.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [catechistsData, searchTerm]);

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row flex-wrap justify-between items-center mb-4 gap-4">
        <h2 className="text-2xl font-bold">Quản lý Giáo lý viên</h2>
        <div className="flex-grow sm:flex-grow-0 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Tìm theo Họ và Tên..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm w-full sm:w-64 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          onClick={handleOpenAddCatechistModal}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 w-full sm:w-auto"
        >
          Thêm Giáo lý viên
        </button>
        {isAddCatechistModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
              <h3 className="text-xl font-bold mb-4">Thêm Giáo lý viên mới</h3>
              {addCatechistError && <p className="text-red-500 mb-3 text-sm">{addCatechistError}</p>}
              <form onSubmit={handleAddNewCatechistSubmit}>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700">ID Code (Tự động)</label>
                  <input type="text" name="idCode" value={newCatechist.idCode} readOnly className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm cursor-not-allowed" />
                </div>
                <div className="mb-3">
                  <label htmlFor="holyNameModal" className="block text-sm font-medium text-gray-700">Tên Thánh <span className="text-red-500">*</span></label>
                  <input type="text" name="holyName" id="holyNameModal" value={newCatechist.holyName} onChange={handleNewCatechistChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
                <div className="mb-3">
                  <label htmlFor="fullNameModal" className="block text-sm font-medium text-gray-700">Họ và Tên <span className="text-red-500">*</span></label>
                  <input type="text" name="fullName" id="fullNameModal" value={newCatechist.fullName} onChange={handleNewCatechistChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
                <div className="mb-3">
                  <label htmlFor="usernameModal" className="block text-sm font-medium text-gray-700">Username <span className="text-red-500">*</span></label>
                  <input type="text" name="username" id="usernameModal" value={newCatechist.username} onChange={handleNewCatechistChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
                <p className="text-xs text-gray-500 mb-4">Mật khẩu mặc định sẽ là "Annhon@1965". Giáo lý viên có thể đổi mật khẩu sau khi đăng nhập.</p>
                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setIsAddCatechistModalOpen(false)} disabled={isAddingCatechist} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Hủy</button>
                  <button type="submit" disabled={isAddingCatechist} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isAddingCatechist ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`}>{isAddingCatechist ? 'Đang thêm...' : 'Thêm Giáo lý viên'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-2 text-left">ID Code</th>
              <th className="border p-2 text-left">Tên Thánh</th>
              <th className="border p-2 text-left">Họ và Tên</th>
              <th className="border p-2 text-left">Username</th>
              <th className="border p-2 text-left">Đứng lớp</th>
              <th className="border p-2 text-left">Quyền admin</th>
              <th className="border p-2 text-left">Trưởng khối</th>
            </tr>
          </thead>
          <tbody>
            {filteredCatechists.length > 0 ? (
              filteredCatechists.map((catechist, index) => (
                <tr key={catechist.idCode || `glv-${index}-${catechist.username}`} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border p-2">{catechist.idCode}</td>
                  <td className="border p-2">{catechist.holyName}</td>
                  <td className="border p-2">{catechist.fullName}</td>
                  <td className="border p-2">{catechist.username}</td>
                  <td className="border p-2">
                    <select
                      value={catechist.className || ''}
                      onChange={(e) => handleUpdateCatechist(catechist.idCode, 'className', e.target.value)}
                      disabled={isUpdating[`${catechist.idCode}-className`]}
                      className="w-full p-1 border rounded focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {classOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="border p-2">{catechist.adminRole}</td>
                  <td className="border p-2">
                    <select
                      value={catechist.blockLeader || ''}
                      onChange={(e) => handleUpdateCatechist(catechist.idCode, 'blockLeader', e.target.value)}
                      disabled={isUpdating[`${catechist.idCode}-blockLeader`]}
                      className="w-full p-1 border rounded focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {blockLeaderOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center border p-4">
                  {searchTerm ? 'Không tìm thấy giáo lý viên nào phù hợp.' : 'Không có dữ liệu giáo lý viên hoặc chưa tải xong.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default CatechistManager;